import { createSelector } from '@reduxjs/toolkit'
import { EditorElement, FeedbackMode, SectionTypeEnum } from '@vedalib/editor/lib/elements'
import lodash from 'lodash'
import * as R from 'ramda'

import { getShellByElementId } from 'components/Editor/EditorContext/selectors/block'
import { testConfig, testElementTypes } from 'components/Editor/EditorElements/pickerConfig/helpers'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import { EMPTY_OBJECT } from 'constants/commonConstans'
import { BlockModeEnum } from 'gql/__generated__/graphql'
import type {
  IProjectContext,
  Block,
  ElementState,
  MapType,
  Section,
} from 'services/Store/Project/types'

import { getBlock, getBlocks, getBlockState, getSections } from '..'
import { BlockMode } from '../../enums'
import { elementsLens } from '../../lenses'
import type { IIncompleteElement } from '../../reducer'

const getElements = (state: IProjectContext) => R.view(elementsLens)(state)
const collectIds = R.compose(
  R.keys,
  R.reduce<Block, MapType<ElementState>>((acc, block) => ({ ...acc, ...block?.elements }), {}),
)

export const getActiveNodeId = createSelector(
  ({ state }: IProjectContext) =>
    state.editor.selectedNodes.length === 1 ? state.editor.selectedNodes[0] : null,
  ({ state }: IProjectContext) => state.comments.opened?.nodeId,
  (nodeId, openedNodeId) => nodeId || openedNodeId,
)

export const getSectionElementsState = (state: IProjectContext, sectionId: string) => {
  const elements = R.view(elementsLens)(state)
  const blocks = getBlocks(state, sectionId)
  return lodash.pick(elements, collectIds(blocks))
}

export const getBlockElementsState = createSelector(
  getElements,
  (state: IProjectContext, blockId?: string) => getBlock(state, { id: blockId }),
  (elements, block) =>
    lodash.pick(elements, R.keys(block?.elements || EMPTY_OBJECT)) as MapType<ElementState>,
)

export const getElementState = createSelector(
  (state: IProjectContext) => state.state.elements,
  (_state: IProjectContext, id: string) => id,
  (elements, id) => elements[id] as ElementState | undefined,
)
// export const getElementState = (state: IProjectContext, id: string) =>
//   R.view(elementLens(id))(state)

export const getAllIncompleteElements = (state: IProjectContext) => {
  const result: IIncompleteElement[] = []

  const sections = getSections(state)
  const elements = R.view(elementsLens)(state)

  Object.values(sections).forEach((section: Section) => {
    const sectionId = section.id
    section.blocks?.forEach((block: Block) => {
      if (block && block.elements) {
        const blockState = getBlockState(state, block.uuid)
        Object.keys(block.elements)?.forEach((elementId) => {
          const elementState = elements[elementId]
          const element = block.elements[elementId]
          const nodeId = getShellByElementId(block, elementId) || ''
          const isTestElement =
            block.mode === BlockModeEnum.questions &&
            testElementTypes.some((el) => el === element.type)

          if (blockState?.incomplete) {
            if ((block.isMandatory && elementState?.incomplete) || isTestElement) {
              result.push({
                sectionId,
                blockId: block.uuid,
                nodeId,
                elementId,
              })
            }
          }
        })
      }
    })
  })

  return result
}

export const getIncompleteElementsByBlocks = (
  state: IProjectContext,
  blocks: string[],
): IIncompleteElement[] => {
  const allIncomplete = getAllIncompleteElements(state)
  const blockIdSet = new Set(blocks)
  const grouped: Record<string, typeof allIncomplete> = {}
  allIncomplete.forEach((item) => {
    if (blockIdSet.has(item.blockId)) {
      if (!grouped[item.blockId]) {
        grouped[item.blockId] = []
      }
      grouped[item.blockId].push(item)
    }
  })

  return blocks.flatMap((blockId) => grouped[blockId] || [])
}

export const isFeedbackEnabledInTestElementResult = (
  testElements: EditorElement[],
  section: Section,
  allowSectionsFeedback: SectionTypeEnum[] = [SectionTypeEnum.test],
) => {
  const sectionFeedbackMode = section.test?.feedbackMode

  const hasFeedbackEl = testElements.some(
    ({ type, value }) => type === 'testFeedback' && value?.show,
  )

  if (
    !allowSectionsFeedback?.includes(section.type) ||
    (sectionFeedbackMode === FeedbackMode.HIDE_ALL && !hasFeedbackEl)
  ) {
    return false
  }

  const sectionShowIcon = sectionFeedbackMode !== FeedbackMode.HIDE_ICON
  const sectionShowFeedback = sectionFeedbackMode !== FeedbackMode.HIDE_FEEDBACK

  return testElements.some((element) => {
    const { answerFeedback, displayFeedback } = getFeedbackValue(element.value || {})
    return Boolean(
      (answerFeedback && sectionShowIcon) ||
        (displayFeedback && sectionShowFeedback) ||
        hasFeedbackEl,
    )
  })
}

export const getTestElementsByBlockId = createSelector(
  [(state: IProjectContext, blockId: string) => getBlock(state, { id: blockId })],
  (block) => {
    if (!block || block?.mode !== BlockModeEnum.questions) {
      return []
    }
    return Object.values<EditorElement>(block.elements).filter((element) =>
      testConfig[BlockMode.questions]?.some((elType) => elType === element.type),
    )
  },
)

const TEST_SECTIONS = [SectionTypeEnum.test]
const TEST_AND_LANDING_SECTIONS = [SectionTypeEnum.test, SectionTypeEnum.landing]

export const isFeedbackEnabledInTestElement = createSelector(
  [
    getTestElementsByBlockId,
    (_state: IProjectContext, _blockId: string, section: Section) => section,
  ],
  (testElements, section) =>
    isFeedbackEnabledInTestElementResult(testElements, section, TEST_SECTIONS),
)

export const isFeedbackEnabledInElement = createSelector(
  [
    getTestElementsByBlockId,
    (_state: IProjectContext, _blockId: string, section: Section) => section,
  ],
  (testElements, section) =>
    isFeedbackEnabledInTestElementResult(testElements, section, TEST_AND_LANDING_SECTIONS),
)
